import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { subscriptionsAPI, platformAPI, fileUrl } from '../../services/api';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Printer, Send, Save, CreditCard, CheckCircle, Clock,
  Calendar, DollarSign, Building2, FileText,
} from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—';

const PRINT_CSS = `
@media print {
  body * { visibility: hidden !important; }
  .payment-print-area, .payment-print-area * { visibility: visible !important; }
  .payment-print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 0; margin: 0; }
  .no-print { display: none !important; }
  @page { margin: 12mm; size: A4; }
}
`;

export default function SuperAdminPaymentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [platform, setPlatform] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Editable fields
  const [paymentMethod, setPaymentMethod] = useState('manual');
  const [paymentRef, setPaymentRef] = useState('');
  const [notes, setNotes] = useState('');
  const [amount, setAmount] = useState(0);
  const [dueDate, setDueDate] = useState('');

  useEffect(() => {
    if (!document.getElementById('payment-print-css')) {
      const style = document.createElement('style');
      style.id = 'payment-print-css';
      style.textContent = PRINT_CSS;
      document.head.appendChild(style);
    }
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [invData, platData] = await Promise.all([
        subscriptionsAPI.invoices().then(inv => inv.find(i => String(i.id) === String(id))),
        platformAPI.getPublicSettings().catch(() => ({})),
      ]);
      if (invData) {
        setInvoice(invData);
        setAmount(invData.amount || 0);
        setPaymentRef(invData.payment_reference || '');
        setNotes(invData.notes || '');
        setPaymentMethod(invData.payment_reference || 'manual');
      }
      setPlatform(platData);
    } catch (err) {
      toast.error('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    setSaving(true);
    try {
      await subscriptionsAPI.payInvoice(id, {
        payment_method: paymentMethod,
        payment_reference: paymentRef,
        notes: notes,
        amount: amount,
      });
      toast.success('Payment recorded successfully!');
      loadData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkUnpaid = async () => {
    if (!window.confirm('Mark this invoice as unpaid?')) return;
    try {
      await subscriptionsAPI.unpayInvoice(id);
      toast.success('Invoice marked as unpaid');
      loadData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSendEmail = async () => {
    try {
      const { emailAPI } = await import('../../services/api');
      await emailAPI.send({
        to: invoice.company_email || 'admin@company.com',
        subject: 'Payment Receipt #' + id + ' — ' + (platform.platform_name || 'LedgerPro'),
        html: generateReceiptHTML(invoice, platform, { paymentMethod, paymentRef, notes, amount }),
      });
      toast.success('Receipt sent to ' + (invoice.company_name || 'company'));
    } catch (err) {
      toast.error(err.message || 'Failed to send');
    }
  };

  if (loading) return <div className="spinner" style={{ margin: '80px auto' }} />;
  if (!invoice) return <div style={{ padding: 40, textAlign: 'center' }}><p>Invoice not found</p><button className="sa-btn sa-btn-outline" onClick={() => navigate(-1)}>Go Back</button></div>;

  const isPaid = invoice.status === 'paid';
  const statusColor = isPaid ? '#16a34a' : '#f59e0b';

  return (
    <div>
      {/* Top toolbar */}
      <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <button className="sa-btn sa-btn-ghost" onClick={() => navigate(-1)}>
          <ArrowLeft size={14} /> Back
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={handlePrint}>
            <Printer size={14} /> Print
          </button>
          <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={handleSendEmail} style={{ color: '#8b5cf6', borderColor: '#8b5cf6' }}>
            <Send size={14} /> Send to Company
          </button>
        </div>
      </div>

      <div className="payment-print-area" style={{ background: '#fff', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden', maxWidth: 900, margin: '0 auto' }}>
        {/* HEADER */}
        <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)', color: '#fff', padding: '28px 36px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {platform.logo_path ? (
              <img src={fileUrl(platform.logo_path)} alt="Logo" style={{ height: 50, width: 50, objectFit: 'contain', borderRadius: 8, background: '#fff', padding: 3 }} />
            ) : (
              <div style={{ width: 50, height: 50, borderRadius: 8, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800 }}>LP</div>
            )}
            <div>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>{platform.platform_name || 'LedgerPro'}</h1>
              {platform.company_address && <p style={{ margin: '2px 0 0', fontSize: 11, opacity: 0.8 }}>{platform.company_address}</p>}
              {platform.company_phone && <p style={{ margin: '2px 0 0', fontSize: 11, opacity: 0.8 }}>Tel: {platform.company_phone}</p>}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ display: 'inline-block', padding: '5px 14px', borderRadius: 16, background: statusColor, color: '#fff', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
              {isPaid ? 'PAID' : 'PENDING'}
            </div>
            <p style={{ margin: '8px 0 0', fontSize: 18, fontWeight: 800 }}>PAYMENT RECEIPT</p>
            <p style={{ margin: '2px 0 0', fontSize: 12, opacity: 0.8 }}>Invoice #{String(invoice.id).padStart(5, '0')}</p>
          </div>
        </div>

        {/* BODY */}
        <div style={{ padding: '28px 36px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
          {/* Left: Payment Info */}
          <div>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Payment Details</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {[
                  ['Company', invoice.company_name],
                  ['Plan', invoice.plan_name],
                  ['Billing Cycle', invoice.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'],
                  ['Invoice Date', fmtDate(invoice.created_at)],
                  ['Period', fmtDate(invoice.period_start) + ' to ' + fmtDate(invoice.period_end)],
                  ['Amount', fmt(invoice.amount)],
                ].map(([label, value]) => (
                  <tr key={label} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 0', fontSize: 12, color: '#64748b', width: 130 }}>{label}</td>
                    <td style={{ padding: '8px 0', fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Right: Editable Payment Form */}
          <div>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
              {isPaid ? 'Payment Confirmed' : 'Process Payment'}
            </h3>

            {isPaid ? (
              <div style={{ padding: 16, borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <CheckCircle size={18} style={{ color: '#16a34a' }} />
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#16a34a' }}>Payment Received</span>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr><td style={{ padding: '4px 0', fontSize: 12, color: '#166534' }}>Amount</td><td style={{ padding: '4px 0', fontSize: 13, fontWeight: 700, color: '#166534' }}>{fmt(invoice.amount)}</td></tr>
                    <tr><td style={{ padding: '4px 0', fontSize: 12, color: '#166534' }}>Paid At</td><td style={{ padding: '4px 0', fontSize: 12, color: '#166534' }}>{fmtDate(invoice.paid_at)}</td></tr>
                    {invoice.payment_reference && <tr><td style={{ padding: '4px 0', fontSize: 12, color: '#166534' }}>Reference</td><td style={{ padding: '4px 0', fontSize: 12, color: '#166534' }}>{invoice.payment_reference}</td></tr>}
                  </tbody>
                </table>
                <button className="no-print sa-btn sa-btn-outline sa-btn-sm" onClick={handleMarkUnpaid} style={{ marginTop: 12, color: '#dc2626', borderColor: '#dc2626', fontSize: 11 }}>
                  Mark as Unpaid
                </button>
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Amount ($)</label>
                  <input className="sa-input" type="number" step="0.01" value={amount} onChange={e => setAmount(parseFloat(e.target.value) || 0)} style={{ width: '100%' }} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Payment Method</label>
                  <select className="sa-input sa-select" value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} style={{ width: '100%' }}>
                    <option value="manual">Manual / Bank Transfer</option>
                    <option value="cash">Cash</option>
                    <option value="check">Check</option>
                    <option value="credit_card">Credit Card</option>
                    <option value="paypal">PayPal</option>
                    <option value="stripe">Stripe</option>
                  </select>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Payment Reference</label>
                  <input className="sa-input" value={paymentRef} onChange={e => setPaymentRef(e.target.value)} placeholder="Transaction ID, check #, etc." style={{ width: '100%' }} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Notes</label>
                  <textarea className="sa-input" rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional payment notes..." style={{ width: '100%' }} />
                </div>
                <button className="no-print sa-btn sa-btn-primary" onClick={handleMarkPaid} disabled={saving} style={{ width: '100%', justifyContent: 'center' }}>
                  {saving ? 'Processing...' : <><CheckCircle size={14} /> Record Payment — {fmt(amount)}</>}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0', padding: '16px 36px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <p style={{ margin: 0, fontSize: 11, color: '#94a3b8' }}>{platform.platform_name || 'LedgerPro'} — Professional Accounting Platform</p>
            {platform.company_website && <p style={{ margin: '2px 0 0', fontSize: 11, color: '#94a3b8' }}>{platform.company_website}</p>}
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ margin: 0, fontSize: 11, color: '#94a3b8' }}>This is a computer-generated document. No signature required.</p>
            <p style={{ margin: '2px 0 0', fontSize: 11, color: '#94a3b8' }}>Generated on {fmtDate(new Date().toISOString())}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function generateReceiptHTML(invoice, platform, payment) {
  const isPaid = invoice.status === 'paid';
  return `
<!DOCTYPE html><html><body style="margin:0;padding:0;background:#f1f5f9;font-family:'Segoe UI',Tahoma,sans-serif;">
<div style="max-width:600px;margin:20px auto;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 4px 20px rgba(0,0,0,0.08);">
  <div style="background:linear-gradient(135deg,#0f172a,#1e3a5f);color:#fff;padding:24px 28px;display:flex;align-items:center;gap:12px;">
    <div style="width:44px;height:44px;border-radius:8px;background:rgba(255,255,255,0.15);display:inline-flex;align-items:center;justify-content:center;font-size:16px;font-weight:800;">LP</div>
    <div><h1 style="margin:0;font-size:18px;font-weight:800;">${platform.platform_name || 'LedgerPro'}</h1>
    ${platform.company_address ? '<p style="margin:2px 0 0;font-size:10px;opacity:0.8;">' + platform.company_address + '</p>' : ''}</div>
    <div style="margin-left:auto;text-align:right;">
      <span style="background:${isPaid ? '#16a34a' : '#f59e0b'};color:#fff;padding:3px 12px;border-radius:14px;font-size:10px;font-weight:700;">${isPaid ? 'PAID' : 'PENDING'}</span>
      <p style="margin:6px 0 0;font-size:11px;opacity:0.8;">Receipt #${String(invoice.id).padStart(5, '0')}</p>
    </div>
  </div>
  <div style="padding:24px 28px;">
    <p style="font-size:12px;color:#64748b;margin:0 0 4px;">Bill To</p>
    <p style="font-size:15px;font-weight:700;color:#0f172a;margin:0;">${invoice.company_name}</p>
    <table style="width:100%;border-collapse:collapse;margin:20px 0;">
      <tr><td style="padding:8px 12px;font-size:11px;color:#64748b;border-bottom:1px solid #f1f5f9;">Plan</td><td style="padding:8px 12px;font-size:12px;font-weight:600;border-bottom:1px solid #f1f5f9;">${invoice.plan_name}</td></tr>
      <tr><td style="padding:8px 12px;font-size:11px;color:#64748b;border-bottom:1px solid #f1f5f9;">Cycle</td><td style="padding:8px 12px;font-size:12px;border-bottom:1px solid #f1f5f9;">${invoice.billing_cycle}</td></tr>
      <tr><td style="padding:8px 12px;font-size:11px;color:#64748b;border-bottom:1px solid #f1f5f9;">Period</td><td style="padding:8px 12px;font-size:12px;border-bottom:1px solid #f1f5f9;">${fmtDate(invoice.period_start)} to ${fmtDate(invoice.period_end)}</td></tr>
      <tr><td style="padding:8px 12px;font-size:11px;color:#64748b;font-weight:700;">Total</td><td style="padding:8px 12px;font-size:14px;font-weight:800;color:#0f172a;">${fmt(invoice.amount)}</td></tr>
    </table>
    ${isPaid ? '<div style="text-align:center;padding:10px;border-radius:8px;background:#f0fdf4;border:1px solid #bbf7d0;"><p style="margin:0;font-size:12px;font-weight:700;color:#16a34a;">✓ Payment Received ' + fmtDate(invoice.paid_at) + '</p></div>' : '<div style="text-align:center;padding:10px;border-radius:8px;background:#fffbeb;border:1px solid #fde68a;"><p style="margin:0;font-size:12px;font-weight:700;color:#92400e;">Payment Pending</p></div>'}
  </div>
  <div style="background:#f8fafc;border-top:1px solid #e2e8f0;padding:14px 28px;text-align:center;">
    <p style="margin:0;font-size:10px;color:#94a3b8;">${platform.platform_name || 'LedgerPro'} — Professional Accounting Platform</p>
  </div>
</div></body></html>`;
}
